import { MapPin, X, ArrowRight, Database, Globe, TrendingUp, Zap } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { getClimateZone } from '../utils/calculations';

interface DetectionSummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: () => void;
  latitude: number;
  longitude: number;
  locationName?: string;
  systemType: string;
  panelType: string;
  capacity: number;
  detectedLosses: {
    soiling: number;
    shading: number;
    dcLoss: number;
    acLoss: number;
    inverterLoss: number;
    degradation: number;
  };
  previousLosses: {
    soiling: number;
    shading: number;
    dcLoss: number;
    acLoss: number;
    inverterLoss: number;
    degradation: number;
  };
}

export function DetectionSummaryModal({
  isOpen,
  onClose,
  onApply,
  latitude,
  longitude,
  locationName,
  systemType,
  panelType,
  capacity,
  detectedLosses,
  previousLosses,
}: DetectionSummaryModalProps) {
  const climateZone = getClimateZone(latitude, longitude);
  
  // Calculate adjustments
  const changes = [
    { name: 'Soiling', before: previousLosses.soiling, after: detectedLosses.soiling },
    { name: 'Shading', before: previousLosses.shading, after: detectedLosses.shading },
    { name: 'DC Loss', before: previousLosses.dcLoss, after: detectedLosses.dcLoss },
    { name: 'AC Loss', before: previousLosses.acLoss, after: detectedLosses.acLoss },
    { name: 'Inverter Loss', before: previousLosses.inverterLoss, after: detectedLosses.inverterLoss },
    { name: 'Degradation', before: previousLosses.degradation, after: detectedLosses.degradation },
  ].filter(item => item.before !== item.after);
  
  // Climate descriptions
  const climateInfo = {
    desert: {
      icon: '🏜️',
      name: 'Desert/Arid Climate',
      description: 'High solar irradiation with significant dust and sand accumulation',
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200',
    },
    tropical: {
      icon: '🌴',
      name: 'Tropical/Humid Climate',
      description: 'High rainfall provides natural panel cleaning, lower soiling losses',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
    },
    temperate: {
      icon: '🌤️',
      name: 'Temperate Climate',
      description: 'Moderate conditions with balanced seasonal performance',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
    },
    cold: {
      icon: '❄️',
      name: 'Cold/Snowy Climate',
      description: 'Lower sun angles with seasonal snow coverage considerations',
      color: 'text-cyan-600',
      bgColor: 'bg-cyan-50',
      borderColor: 'border-cyan-200',
    },
  };
  
  const climate = climateInfo[climateZone];
  
  // Calculate confidence score
  let confidence = 60;
  if (climateZone !== 'temperate') confidence += 15;
  if (capacity >= 500) confidence += 15;
  else if (capacity >= 50) confidence += 10;
  else confidence += 5;
  if (locationName) confidence += 10;
  confidence = Math.min(95, confidence);
  
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-50"
          />
          
          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', duration: 0.3 }}
              className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-[#4F819F] to-[#6B9FB8] text-white p-6 relative">
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 p-1 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
                
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <Zap className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">AI Detection Results</h3>
                    <p className="text-sm text-white/80">Climate-optimized system loss analysis</p>
                  </div>
                </div>
              </div>
              
              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
                {/* Climate Zone */}
                <div className={`${climate.bgColor} ${climate.borderColor} border-2 rounded-xl p-4 mb-6`}>
                  <div className="flex items-start gap-3">
                    <span className="text-3xl">{climate.icon}</span>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h5 className={`font-bold text-lg ${climate.color}`}>{climate.name}</h5>
                        <span className="text-xs text-slate-500">
                          {latitude.toFixed(2)}°, {longitude.toFixed(2)}°
                        </span>
                      </div>
                      <p className="text-sm text-slate-700 mb-2">{climate.description}</p>
                      {locationName && (
                        <p className="text-sm text-slate-600 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {locationName}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* System Info */}
                <div className="grid grid-cols-3 gap-3 mb-6">
                  <div className="bg-slate-50 rounded-lg p-3 text-center">
                    <div className="text-xs text-slate-500 mb-1">System Type</div>
                    <div className="font-semibold text-slate-900 capitalize">{systemType}</div>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3 text-center">
                    <div className="text-xs text-slate-500 mb-1">Panel Tech</div>
                    <div className="font-semibold text-slate-900 capitalize">{panelType}</div>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3 text-center">
                    <div className="text-xs text-slate-500 mb-1">Capacity</div>
                    <div className="font-semibold text-slate-900">{capacity} kWp</div>
                  </div>
                </div>
                
                {/* Changes Preview */}
                {changes.length > 0 && (
                  <div className="mb-6">
                    <h5 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <ArrowRight className="w-4 h-4 text-[#4F819F]" />
                      Proposed Changes ({changes.length})
                    </h5>
                    
                    <div className="space-y-2">
                      {changes.map((change, index) => {
                        const diff = change.after - change.before;
                        const isIncrease = diff > 0;
                        
                        return (
                          <div key={index} className="bg-slate-50 rounded-lg p-3">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium text-slate-900">{change.name}</span>
                              <span className={`text-sm font-semibold ${isIncrease ? 'text-red-600' : 'text-green-600'}`}>
                                {isIncrease ? '+' : ''}{diff.toFixed(1)}%
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-xs">
                              <span className="text-slate-500">{change.before.toFixed(1)}%</span>
                              <ArrowRight className="w-3 h-3 text-slate-400" />
                              <span className="font-semibold text-slate-900">{change.after.toFixed(1)}%</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {changes.length === 0 && (
                  <div className="mb-6 text-center py-6 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-800">
                      ✅ Current values are already optimal for this climate zone
                    </p>
                  </div>
                )}
                
                {/* Methodology & Confidence */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3 text-xs flex-wrap">
                      <div className="flex items-center gap-1">
                        <Database className="w-3 h-3 text-[#4F819F]" />
                        <span className="text-slate-600">NASA POWER</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Globe className="w-3 h-3 text-[#4F819F]" />
                        <span className="text-slate-600">Climate-Aware</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <TrendingUp className="w-3 h-3 text-[#4F819F]" />
                        <span className="text-slate-600">Industry Benchmarks</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-600">Detection Confidence:</span>
                    <div className="flex-1 flex items-center gap-2">
                      <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-[#4F819F] to-green-500 rounded-full transition-all"
                          style={{ width: `${confidence}%` }}
                        />
                      </div>
                      <span className="text-sm font-bold text-slate-900 w-10">{confidence}%</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Footer Actions */}
              <div className="border-t border-slate-200 p-6 bg-slate-50 flex gap-3">
                <button
                  onClick={onClose}
                  className="flex-1 px-6 py-3 border-2 border-slate-300 text-slate-700 font-semibold rounded-xl hover:bg-slate-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={onApply}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-[#4F819F] to-[#6B9FB8] text-white font-semibold rounded-xl hover:shadow-lg transition-all"
                >
                  Apply Changes
                </button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}